// The Revise validator: one module, scoped by board and profile
// (KULA-REVISE-LAB-ENGINE-PLAN.md §4).
//
// Pure and server-safe. The MCP tool runs it before it writes and blocks the
// whole write on any `block`; the builder runs it to show the author the same
// report; the migration audit runs it over every extracted file. A block is a
// rule one of the authors named as non-negotiable or a fault that would
// silently mis-score a learner. Everything else warns.
//
// Decision 3 (Luke, 14 Sept 2026): NSC keeps Dylan's rules unchanged; IEB
// keeps Leah's. Where the two disagree (length parity as a ratio vs "correct
// never uniquely the longest"), both are here and each fires for its board.

import type { QuestionScreen, ReviseRun, ReviseScreen, ReviseProfile, ReviseBoard } from './types';
import { NSC_SKILLS, REVISE_BOARDS, REVISE_PROFILES, REVISE_SCREEN_TYPES, SOURCE_PAPER_FORMAT, SOURCE_PAPER_ROLES, assessedQuestions } from './types';
import { workshopIssues, workshopProseFields } from './workshop/validate';
import { sourcePaperIssues, sourcePaperProseFields } from './sourcePaper/validate';
import { isLowerOrder, isValidTag, nscLevelOf, tagSetsFor } from './tags';
import {
  findBanned,
  hasConsequence,
  hasDash,
  hasSecondPerson,
  levelReveal,
  longSentences,
  optRefs,
  overlapRatio,
  stripHtml,
  wordCount,
} from './text';
import { marksTotal } from './scoring';

export type Severity = 'block' | 'warn';

export interface ReviseIssue {
  code: string;
  severity: Severity;
  /** Screen id the issue anchors to, when it has one. */
  screenId?: string;
  message: string;
}

export interface ReviseReport {
  profile: ReviseProfile;
  board: ReviseBoard;
  screens: number;
  questions: number;
  assessed: number;
  marksTotal: number;
  /** Share of band-eligible assessed items that are lower order, 0–100. Lesson profile. */
  lowerOrderPct?: number;
  /** Items excluded from the band because their tag set has no lower-order half. */
  bandExcluded?: number;
  /** Marks per NSC level. Paper profile. */
  levelSplit?: Record<'L1' | 'L2' | 'L3', number>;
  /** Guides still on the generic example. Paper profile. */
  genericGuides?: string[];
  /** Freshness overlap, 0–1. Paper profile. */
  freshness?: number;
}

export interface ReviseValidation {
  ok: boolean;
  blocks: ReviseIssue[];
  warnings: ReviseIssue[];
  report: ReviseReport;
}

/** NSC level weighting for a 50-mark source-based question. */
export const NSC_LEVEL_TARGET: Record<'L1' | 'L2' | 'L3', number> = { L1: 15, L2: 20, L3: 15 };
/** Lower-order share Leah aims for per lesson. */
export const IEB_LOWER_ORDER_TARGET = 60;
/** Distractor parity window (Dylan): each wrong option within 70%–143% of the correct one's words. */
export const PARITY_MIN = 0.7;
export const PARITY_MAX = 1.43;
export const SENTENCE_MAX_WORDS = 24;
export const SHORT_MAX_CHARS = 30;
export const FRESHNESS_MIN = 0.2;
export const IMAGE_MIN_LONG_EDGE = 600;

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const isStr = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/** Structural pre-check on an unknown value. Returns the blocks; empty means `v` is at least shaped like a run. */
export function structuralIssues(v: unknown): ReviseIssue[] {
  const blocks: ReviseIssue[] = [];
  const block = (code: string, message: string) => blocks.push({ code, severity: 'block', message });
  if (!isObj(v)) {
    block('schema.run', 'The run must be a JSON object.');
    return blocks;
  }
  if (v.version !== 1) block('schema.version', 'run.version must be 1.');
  if (!(REVISE_PROFILES as readonly string[]).includes(String(v.profile))) {
    block('schema.profile', `run.profile must be ${REVISE_PROFILES.join(' | ')} (got ${JSON.stringify(v.profile)}).`);
  }
  if (!(REVISE_BOARDS as readonly string[]).includes(String(v.board))) block('schema.board', `run.board must be ${REVISE_BOARDS.join(' | ')} (got ${JSON.stringify(v.board)}).`);
  if (!isObj(v.cover)) block('schema.cover', 'run.cover is required.');
  else {
    if (typeof v.cover.title !== 'string' || !v.cover.title.trim()) block('schema.cover.title', 'cover.title is required.');
    if (typeof v.cover.eyebrow !== 'string') block('schema.cover.eyebrow', 'cover.eyebrow is required.');
    if (!Array.isArray(v.cover.lines)) block('schema.cover.lines', 'cover.lines must be an array of strings.');
    if (typeof v.cover.begin !== 'string') block('schema.cover.begin', 'cover.begin (the button label) is required.');
  }
  // A workshop's steps come from run.workshop, and a source paper's from
  // run.sourcePaper; their screens are empty and they have no score bands, so
  // those two rules do not apply to them.
  const isWorkshop = v.profile === 'workshop';
  const isSourcePaper = v.profile === 'source-paper';
  const ownSteps = isWorkshop || isSourcePaper;
  if (!isObj(v.results)) block('schema.results', 'run.results is required.');
  else if (!ownSteps && (!Array.isArray(v.results.bands) || v.results.bands.length === 0)) block('schema.results.bands', 'results.bands must have at least one band.');
  if (!Array.isArray(v.screens) || (!ownSteps && v.screens.length === 0)) block('schema.screens', ownSteps ? `run.screens must be an array (empty for a ${isWorkshop ? 'workshop' : 'source paper'}).` : 'run.screens must be a non-empty array.');
  else {
    const ids = new Set<string>();
    v.screens.forEach((s, i) => {
      if (!isObj(s)) return block('schema.screen', `screens[${i}] is not an object.`);
      if (typeof s.id !== 'string' || !s.id) block('schema.screen.id', `screens[${i}] has no id.`);
      else if (ids.has(s.id)) block('schema.screen.id', `Duplicate screen id "${s.id}".`);
      else ids.add(s.id);
      if (!REVISE_SCREEN_TYPES.includes(s.type as never)) block('schema.screen.type', `screens[${i}] has unknown type ${JSON.stringify(s.type)}.`);
    });
  }
  if (v.profile === 'paper' && !isObj(v.paper)) block('schema.paper', 'A paper run needs run.paper (paperRef, totalMarks).');
  if (v.profile === 'check' && !isObj(v.check)) block('schema.check', 'A check run needs run.check (sitting, pairKey).');
  if (isWorkshop && !isObj(v.workshop)) block('schema.workshop', 'A workshop run needs run.workshop (the question, its editions and their content).');
  else if (isWorkshop) {
    // The player reads these the moment it mounts, before any step; a run
    // missing them must be refused here, not crash the slide.
    const w = v.workshop as Record<string, unknown>;
    const b = w.brand as Record<string, unknown> | undefined;
    if (!isObj(b) || !isStr(b.line) || !isStr(b.title) || !isStr(b.titleAccent)) block('schema.workshop', 'run.workshop.brand needs line, title and titleAccent.');
    if (!isStr(w.question)) block('schema.workshop', 'run.workshop.question is required.');
  }
  if (isSourcePaper && !isObj(v.sourcePaper)) block('schema.sourcePaper', 'A source-paper run needs run.sourcePaper (the sources, the five questions and their feedback).');
  else if (isSourcePaper) {
    // The player reads these on mount; refuse a run without them rather than crash the slide.
    // Only the shapes the player reads as it renders; the content rules are
    // sourcePaperIssues', run by validateRun, so warnings still show beside them.
    const p = v.sourcePaper as Record<string, unknown>;
    const strList = (x: unknown) => Array.isArray(x) && x.every((s) => typeof s === 'string');
    // What the booklet reads from a body: its attribution, then paragraphs (text) or an image id.
    const body = (b: unknown) => isObj(b) && typeof b.who === 'string' && (b.type === 'text' ? strList(b.paragraphs) : b.type === 'image' && typeof b.imageId === 'string');
    if (
      !isObj(p.sources) ||
      Object.keys(p.sources).length !== SOURCE_PAPER_FORMAT.sources ||
      !Object.values(p.sources).every((s) => isObj(s) && ('variants' in s ? isObj(s.variants) && Object.keys(s.variants).length > 0 && Object.values(s.variants).every(body) : body(s)))
    ) {
      block('schema.sourcePaper', `run.sourcePaper.sources must map each of the ${SOURCE_PAPER_FORMAT.sources} letters to a source (or to its versions).`);
    }
    if (
      !Array.isArray(p.questions) ||
      p.questions.map((q) => (isObj(q) ? q.role : null)).join() !== SOURCE_PAPER_ROLES.join() ||
      !p.questions.every((q) => isObj(q) && isStr(q.id) && isStr(q.n) && Array.isArray(q.sources) && (typeof q.text === 'string' || (isObj(q.text) && isObj(q.text.byVariant))))
    ) {
      block('schema.sourcePaper', `run.sourcePaper.questions must be the ${SOURCE_PAPER_ROLES.length} questions in role order (${SOURCE_PAPER_ROLES.join(', ')}), each with id, n, sources and text.`);
    }
    if (!Array.isArray(p.how) || !p.how.every((h) => isObj(h) && typeof h.head === 'string' && typeof h.text === 'string')) block('schema.sourcePaper', 'run.sourcePaper.how must be a list of {head, text} cards.');
    // The revise step indexes the content pack's cards as soon as it is opened.
    const rv = p.revise as Record<string, unknown> | undefined;
    if (!isObj(rv) || !Array.isArray(rv.cards) || rv.cards.length === 0) block('schema.sourcePaper', 'run.sourcePaper.revise.cards needs at least one card.');
    else if (!rv.cards.every((c) => isObj(c) && typeof c.title === 'string' && strList(c.cues) && strList(c.points) && (c.types === undefined || (Array.isArray(c.types) && c.types.every(isObj))))) {
      block('schema.sourcePaper', 'run.sourcePaper.revise.cards must each have a title, cues and points.');
    }
  }
  return blocks;
}

export function validateRun(run: ReviseRun): ReviseValidation {
  const blocks: ReviseIssue[] = [];
  const warnings: ReviseIssue[] = [];
  const block = (code: string, message: string, screenId?: string) => blocks.push({ code, severity: 'block', message, screenId });
  const warn = (code: string, message: string, screenId?: string) => warnings.push({ code, severity: 'warn', message, screenId });

  const structural = structuralIssues(run);
  const report: ReviseReport = {
    profile: run.profile,
    board: run.board,
    screens: run.screens?.length ?? 0,
    questions: 0,
    assessed: 0,
    marksTotal: 0,
  };
  if (structural.length) {
    return { ok: false, blocks: structural, warnings, report };
  }

  const { board, profile } = run;
  void board;
  const sourceKeys = new Set((run.sources ?? []).map((s) => s.key));
  const imageIds = new Set((run.images ?? []).map((i) => i.id));
  const questions = run.screens.filter((s): s is QuestionScreen => s.type === 'question');
  const assessed = assessedQuestions(run);
  report.questions = questions.length;
  report.assessed = assessed.length;
  report.marksTotal = marksTotal(run);

  // --- Every screen: field checks, references, prose ------------------------
  for (const s of run.screens) {
    checkScreenRefs(s, sourceKeys, imageIds, block);
    for (const [field, text] of proseFields(s)) {
      if (/src=["']?data:/i.test(text)) {
        block('image.inline', `${s.type} "${s.id}" ${field} embeds a base64 image. Images are hosted assets (run.images or a public URL); the data must never carry the bytes.`, s.id);
      }
      if (hasDash(text)) warn('prose.dash', `${s.type} "${s.id}" ${field}: em or en dash. Use a full stop, comma or colon.`, s.id);
      // Leah's register rules apply to every lesson-engine run (both boards);
      // the paper profile is Dylan's and keeps his rules (decision 3).
      if (profile !== 'paper') {
        const banned = findBanned(text);
        if (banned.length) warn('prose.register', `${s.type} "${s.id}" ${field}: banned phrase(s): ${banned.join(', ')}.`, s.id);
        const long = longSentences(text, SENTENCE_MAX_WORDS);
        if (long.length && s.type !== 'source') {
          warn('prose.sentence', `${s.type} "${s.id}" ${field}: ${long.length} sentence(s) over ${SENTENCE_MAX_WORDS} words. First: "${long[0].slice(0, 80)}…"`, s.id);
        }
      }
    }
  }

  // Sources are prose too, except that quoted material is left alone.
  for (const src of run.sources ?? []) {
    if (src.imageId && !imageIds.has(src.imageId)) block('ref.image', `Source "${src.key}" references image "${src.imageId}", which is not in run.images.`);
    if (!src.body && !src.imageId) warn('source.empty', `Source "${src.key}" has neither a body nor an image.`);
    for (const k of src.compares ?? []) {
      if (k === src.key || !sourceKeys.has(k)) block('ref.source', `Source "${src.key}" compares source "${k}", which is not another entry in run.sources.`);
    }
  }
  for (const img of run.images ?? []) {
    if (!img.alt?.trim()) block('image.alt', `Image "${img.id}" has no alt text.`);
    if (img.width && img.height && Math.max(img.width, img.height) < IMAGE_MIN_LONG_EDGE) {
      warn('image.lowres', `Image "${img.id}" is ${img.width}×${img.height}; under ${IMAGE_MIN_LONG_EDGE}px on its long edge it may not be legible on a phone.`);
    }
    if (img.licence === 'flagged') warn('image.licence', `Image "${img.id}" is licence-flagged${img.licenceNote ? `: ${img.licenceNote}` : '.'} The use decision stays with the designer.`);
  }

  // --- Questions ------------------------------------------------------------
  for (const q of questions) checkQuestion(q, run, block, warn);

  // --- Mark This Answer ------------------------------------------------------
  for (const s of run.screens) {
    if (s.type !== 'mta') continue;
    const reveal = levelReveal(s.script) ?? levelReveal(s.label);
    if (reveal) block('mta.reveal', `Mark This Answer "${s.id}" reveals the expected level or quality ("${reveal}"). The learner judges blind.`, s.id);
    if (s.band && /level/i.test(s.band)) block('mta.reveal', `Mark This Answer "${s.id}" band "${s.band}" names a level. Mark range only.`, s.id);
    if (s.criteria.length === 0) block('mta.criteria', `Mark This Answer "${s.id}" has no criteria.`, s.id);
    for (const c of s.criteria) {
      if (!c.feedback?.trim()) block('mta.feedback', `Mark This Answer "${s.id}" criterion "${c.id}" has no feedback.`, s.id);
    }
    if (s.type === 'mta' && s.script && /\{opt\d+\}/.test(s.script)) warn('mta.optref', `Mark This Answer "${s.id}" script contains an {optN} reference; scripts have no options.`, s.id);
  }
  for (const s of run.screens) {
    if (s.type === 'rubric') {
      for (const d of s.dimensions) {
        for (const b of d.bands) {
          if (/\b\d+\s*marks?\b/i.test(stripHtml(b.text)) || /\b\d+\s*marks?\b/i.test(b.label)) {
            block('rubric.marks', `Rubric "${s.id}" names a mark value under dimension "${d.name}". The rubric is holistic; naming the dimension is allowed, a mark value is not.`, s.id);
          }
        }
      }
    }
  }

  // --- Profile rules ----------------------------------------------------------
  if (profile === 'workshop' && run.workshop) {
    for (const issue of workshopIssues(run.workshop)) blocks.push(issue);
    // Leah's register rules reach every string a learner reads in a workshop.
    for (const [field, text] of workshopProseFields(run.workshop)) {
      if (/src=["']?data:/i.test(text)) block('image.inline', `workshop ${field} embeds a base64 image.`);
      if (hasDash(text)) warn('prose.dash', `workshop ${field}: em or en dash. Use a full stop, comma or colon.`);
      const banned = findBanned(text);
      if (banned.length) warn('prose.register', `workshop ${field}: banned phrase(s): ${banned.join(', ')}.`);
      const long = longSentences(text, SENTENCE_MAX_WORDS);
      if (long.length) warn('prose.sentence', `workshop ${field}: ${long.length} sentence(s) over ${SENTENCE_MAX_WORDS} words. First: "${long[0].slice(0, 80)}…"`);
    }
  }
  if (profile === 'source-paper' && run.sourcePaper) {
    if (board !== 'ib') block('sourcePaper.board', `A source paper is IB History Paper 1, so run.board must be ib (got ${board}).`);
    for (const issue of sourcePaperIssues(run.sourcePaper, imageIds)) (issue.severity === 'block' ? blocks : warnings).push(issue);
    // Its marks are the questions', not screens', so the cover claim is checked against those.
    const qs = Array.isArray(run.sourcePaper.questions) ? run.sourcePaper.questions : [];
    report.questions = qs.length;
    report.marksTotal = qs.reduce((n, q) => n + (typeof q?.marks === 'number' ? q.marks : 0), 0);
    // Leah's Paper 1 language rules (spec §4): UK spelling, sentence case, no
    // em dashes. En dashes stay: her content uses them in names and ranges
    // (Hoare–Laval, 1935–1936), and the spec rules out only the em dash.
    for (const [field, text] of sourcePaperProseFields(run.sourcePaper)) {
      if (/src=["']?data:/i.test(text)) block('image.inline', `sourcePaper ${field} embeds a base64 image.`);
      if (/—|&mdash;|&#8212;|&#x2014;/i.test(text)) warn('prose.dash', `sourcePaper ${field}: em dash. Use a full stop, comma or colon.`);
      const banned = findBanned(text);
      if (banned.length) warn('prose.register', `sourcePaper ${field}: banned phrase(s): ${banned.join(', ')}.`);
    }
  }
  if (profile === 'paper') checkPaper(run, report, block, warn);
  if (profile === 'lesson') checkLessonBand(run, report, warn);
  if (profile === 'check') checkCheck(run, block, warn);
  if (profile === 'skeleton') {
    const picks = run.screens.filter((s) => s.type === 'pick' || s.type === 'evidence');
    if (picks.length === 0) block('skeleton.empty', 'A skeleton run needs at least one pick or evidence screen.');
    if (!run.screens.some((s) => s.type === 'frame')) warn('skeleton.frame', 'A skeleton run usually closes with a frame screen showing the assembled plan.');
  }
  for (const s of run.screens) {
    if (s.type === 'pick') {
      if (s.options.length < 2) block('pick.options', `Pick "${s.id}" needs at least two lines to choose from.`, s.id);
      if (!Number.isInteger(s.correct) || s.correct < 0 || s.correct >= s.options.length) block('pick.correct', `Pick "${s.id}": correct index ${s.correct} is out of range.`, s.id);
      if (!s.feedback?.trim()) block('pick.feedback', `Pick "${s.id}" has no feedback.`, s.id);
      if (!s.save?.trim()) block('pick.save', `Pick "${s.id}" has no \`save\` label, so the plan cannot record it.`, s.id);
    }
    if (s.type === 'evidence') {
      if (s.items.length < 2) block('evidence.items', `Evidence "${s.id}" needs at least two facts.`, s.id);
      if (!s.items.some((it) => it.use)) block('evidence.items', `Evidence "${s.id}" has no fact marked \`use\`.`, s.id);
      if (!s.claim?.trim()) block('evidence.claim', `Evidence "${s.id}" has no claim for the facts to support.`, s.id);
      if (!s.feedback?.trim()) block('evidence.feedback', `Evidence "${s.id}" has no feedback.`, s.id);
      for (const it of s.items) if (!it.use && !it.off?.trim()) warn('evidence.off', `Evidence "${s.id}": a fact to leave out should say where it belongs (\`off\`).`, s.id);
    }
  }

  // --- Cover marks must agree with the items (the Angola 26 vs 24 fault) -----
  // A skeleton's cover names the ESSAY's marks, which it plans rather than
  // assesses, so the rule does not apply there.
  const coverMarks = profile === 'skeleton' ? null : coverMarksClaim(run);
  if (coverMarks !== null && coverMarks !== report.marksTotal) {
    // A capstone whose items are all practice may still name the essay's
    // marks on its cover ("Fifty marks. One hour."). That is a claim about the
    // exam, not the items, so it warns; a mismatch against real item marks blocks.
    if (report.marksTotal > 0) block('cover.marks', `The cover claims ${coverMarks} marks but the assessed items total ${report.marksTotal}.`);
    else warn('cover.marks', `The cover names ${coverMarks} marks but no item here carries marks. Fine if it describes the exam; check it is not a leftover.`);
  }

  // Tag sets registered for this board+profile (so an author sees what is allowed).
  if (assessed.length && tagSetsFor(board, profile).length === 0) {
    warn('tags.none', `No tag sets are registered for board ${board} and profile ${profile}; assessed items cannot carry a tag.`);
  }

  return { ok: blocks.length === 0, blocks, warnings, report };
}

// ---------------------------------------------------------------------------

type Block = (code: string, message: string, screenId?: string) => void;

function checkScreenRefs(s: ReviseScreen, sourceKeys: Set<string>, imageIds: Set<string>, block: Block) {
  if (s.type === 'source' && !sourceKeys.has(s.sourceKey)) {
    block('ref.source', `Source screen "${s.id}" references source "${s.sourceKey}", which is not in run.sources.`, s.id);
  }
  if (s.type === 'question' && s.sourceKey && !sourceKeys.has(s.sourceKey)) {
    block('ref.source', `Question "${s.id}" references source "${s.sourceKey}", which is not in run.sources.`, s.id);
  }
  void imageIds;
}

/** Every field the engine renders as Kula's own prose, per screen type. */
export function proseFields(s: ReviseScreen): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const add = (name: string, v: unknown) => {
    if (typeof v === 'string' && v.trim()) out.push([name, v]);
  };
  switch (s.type) {
    case 'story':
      s.lines.forEach((l, i) => add(`lines[${i}]`, l));
      break;
    case 'content':
      add('html', s.html);
      add('remember', s.remember);
      s.terms?.forEach((t, i) => add(`terms[${i}].def`, t.def));
      break;
    case 'stat':
      add('heading', s.heading);
      add('lead', s.lead);
      s.stats.forEach((t, i) => add(`stats[${i}].label`, t.label));
      break;
    case 'source':
      add('html', s.html);
      break;
    case 'carousel':
      s.cards.forEach((c, i) => {
        add(`cards[${i}].lead`, c.lead);
        add(`cards[${i}].body`, c.body);
      });
      break;
    case 'runsum':
      add('heading', s.heading);
      break;
    case 'reveal':
      add('lead', s.lead);
      s.items.forEach((t, i) => {
        add(`items[${i}].lead`, t.lead);
        add(`items[${i}].body`, t.body);
      });
      add('close', s.close);
      break;
    case 'rubric':
      add('intro', s.intro);
      break;
    case 'plan':
      add('intro', s.intro);
      s.rows.forEach((r, i) => {
        add(`rows[${i}].yes`, r.yes);
        add(`rows[${i}].no`, r.no);
      });
      break;
    case 'builder':
      add('lead', s.lead);
      add('close', s.close);
      s.slots.forEach((sl, i) => sl.options.forEach((o, j) => add(`slots[${i}].options[${j}].cost`, o.cost)));
      break;
    case 'mta-intro':
      add('html', s.html);
      break;
    case 'mta':
      s.criteria.forEach((c, i) => add(`criteria[${i}].feedback`, c.feedback));
      break;
    case 'question':
      add('stem', s.stem);
      if (Array.isArray(s.feedback)) s.feedback.forEach((f, i) => add(`feedback[${i}]`, f));
      else add('feedback', s.feedback);
      s.options?.forEach((o, i) => add(`options[${i}]`, o));
      break;
    case 'pick':
      add('intro', s.intro);
      add('feedback', s.feedback);
      s.options.forEach((o, i) => add(`options[${i}]`, o));
      break;
    case 'evidence':
      add('intro', s.intro);
      add('claim', s.claim);
      add('feedback', s.feedback);
      s.items.forEach((it, i) => add(`items[${i}].off`, it.off));
      break;
    case 'frame':
      add('html', s.html);
      break;
  }
  return out;
}

function checkQuestion(q: QuestionScreen, run: ReviseRun, block: Block, warn: Block) {
  const { board, profile } = run;
  const where = `Question "${q.id}"`;

  const feedbackList = Array.isArray(q.feedback) ? q.feedback : q.feedback ? [q.feedback] : [];
  const feedbackText = feedbackList.join(' ');
  if (typeof q.assessed !== 'boolean') block('question.assessed', `${where}: \`assessed\` must be true or false. It is never inferred.`, q.id);
  if (!q.stem?.trim()) block('question.stem', `${where} has no stem.`, q.id);
  if (q.mode !== 'build' && !feedbackText.trim()) block('question.feedback', `${where} has no feedback.`, q.id);
  if (Array.isArray(q.feedback) && q.options && q.feedback.length !== q.options.length) {
    block('question.feedback', `${where}: per-option feedback has ${q.feedback.length} entries for ${q.options.length} options.`, q.id);
  }
  if (q.kind === 'instinct' && q.assessed) block('question.instinct', `${where} is an instinct opener but is marked assessed. Instinct questions never score.`, q.id);
  if (q.mode === 'build' && q.assessed) block('question.build', `${where} is a build question but is marked assessed. Build questions feed the running summary and never score.`, q.id);

  if (q.assessed) {
    if (q.marks === undefined) block('question.marks', `${where} is assessed but carries no mark label. Give it a total or 'practice'.`, q.id);
    else if (q.marks !== 'practice' && (typeof q.marks !== 'number' || q.marks < 0)) block('question.marks', `${where}: marks must be a non-negative number or 'practice'.`, q.id);
    if (!q.short?.trim()) block('question.short', `${where} is assessed but has no \`short\` label for the results recap.`, q.id);
    else if (q.short.length > SHORT_MAX_CHARS) warn('question.short', `${where}: \`short\` is ${q.short.length} characters; keep it under ${SHORT_MAX_CHARS} so the recap fits a phone.`, q.id);
    if (!q.tag) block('question.tag', `${where} is assessed but has no tag.`, q.id);
    else if (!isValidTag(board, profile, q.tag)) {
      block('question.tag', `${where}: tag "${q.tag}" is not on any set registered for ${board.toUpperCase()} / ${profile}. Allowed: ${tagSetsFor(board, profile).map((t) => `${t.label} (${t.values.join(', ')})`).join('; ') || 'none'}.`, q.id);
    }
  }

  // Option-based modes
  if (q.mode === 'mcq' || q.mode === 'multi' || q.mode === 'sentence-mark') {
    const opts = q.options ?? [];
    if (opts.length < 2) block('question.options', `${where}: ${q.mode} needs at least two options.`, q.id);
    if (opts.length > 8) block('question.options', `${where}: more than eight options cannot be lettered.`, q.id);
    const correctIdx = Array.isArray(q.correct) ? q.correct : q.correct === undefined ? [] : [q.correct];
    if (correctIdx.length === 0) block('question.correct', `${where} has no correct index.`, q.id);
    for (const c of correctIdx) {
      if (!Number.isInteger(c) || c < 0 || c >= opts.length) block('question.correct', `${where}: correct index ${c} is out of range for ${opts.length} options.`, q.id);
    }
    if (q.mode === 'mcq' && correctIdx.length > 1 && !q.acceptAny) {
      block('question.acceptAny', `${where}: mcq with several correct indices must set acceptAny (either/or memo) or be mode multi.`, q.id);
    }
    if (q.mode === 'multi') {
      const want = q.maxSelect ?? correctIdx.length;
      if (want !== correctIdx.length) block('question.multi', `${where}: maxSelect (${want}) must equal the number of correct indices (${correctIdx.length}).`, q.id);
      if (q.acceptAny) warn('question.multi', `${where}: acceptAny has no meaning on a multi-select.`, q.id);
    }
    for (const n of optRefs(feedbackText)) {
      if (n >= opts.length) block('question.optref', `${where}: feedback references {opt${n}} but there are only ${opts.length} options.`, q.id);
    }
    const seen = new Set<string>();
    for (const o of opts) {
      const k = stripHtml(o).toLowerCase();
      if (seen.has(k)) warn('question.duplicate', `${where} has two identical options.`, q.id);
      seen.add(k);
    }
    // Banned distractor shapes (Dylan): all/none of the above.
    if (opts.some((o) => /\b(all|none) of the above\b/i.test(o))) warn('question.distractor', `${where}: "all/none of the above" is a banned distractor shape.`, q.id);

    if (q.assessed && opts.length >= 2 && correctIdx.length >= 1) {
      const correctWords = correctIdx.map((i) => wordCount(opts[i] ?? ''));
      const wrong = opts.map((o, i) => ({ i, w: wordCount(o) })).filter(({ i }) => !correctIdx.includes(i));
      // Dylan's parity ratio on the paper profile; Leah's "never uniquely the
      // longest" on every lesson-engine run, whichever board.
      if (profile === 'paper') {
        const ref = correctWords[0] || 1;
        for (const { i, w } of wrong) {
          const ratio = w / ref;
          if (ratio < PARITY_MIN || ratio > PARITY_MAX) {
            warn('question.parity', `${where}: option ${i} is ${w} words against ${ref} for the correct option (ratio ${ratio.toFixed(2)}, window ${PARITY_MIN}–${PARITY_MAX}). Lengthen the distractor rather than cutting the answer.`, q.id);
          }
        }
      } else {
        const maxCorrect = Math.max(...correctWords);
        const longestWrong = Math.max(0, ...wrong.map((x) => x.w));
        if (maxCorrect > longestWrong) {
          warn('question.longest', `${where}: the correct answer is uniquely the longest option (${maxCorrect} words vs ${longestWrong}). Lengthen the distractors, not the other way round.`, q.id);
        }
      }
    }
  }

  if (q.mode === 'order') {
    const items = q.items ?? [];
    if (items.length < 2) block('question.items', `${where}: order needs at least two items.`, q.id);
    const seq = q.sequence ?? [];
    if (seq.length !== items.length) block('question.sequence', `${where}: sequence must list every item index once (${seq.length} of ${items.length}).`, q.id);
    if (new Set(seq).size !== seq.length || seq.some((i) => i < 0 || i >= items.length)) block('question.sequence', `${where}: sequence has a repeated or out-of-range index.`, q.id);
  }
  if (q.mode === 'sort') {
    const items = q.items ?? [];
    const cols = q.cols ?? [];
    if (items.length < 2 || cols.length < 2) block('question.sort', `${where}: sort needs at least two items and two columns.`, q.id);
    const placement = q.placement ?? {};
    for (let i = 0; i < items.length; i++) {
      const col = placement[String(i)];
      if (col === undefined) block('question.placement', `${where}: item ${i} has no column in placement.`, q.id);
      else if (col < 0 || col >= cols.length) block('question.placement', `${where}: item ${i} is placed in column ${col}, but there are ${cols.length} columns.`, q.id);
    }
  }

  // Feedback quality (Leah): second person and a consequence. With per-option
  // feedback, the wrong options are the ones that must carry the cost.
  if (profile !== 'paper' && q.assessed && feedbackList.length) {
    const correctIdx = Array.isArray(q.correct) ? q.correct : q.correct === undefined ? [] : [q.correct];
    const toCheck = Array.isArray(q.feedback) ? q.feedback.filter((_, i) => !correctIdx.includes(i)) : feedbackList;
    if (!toCheck.some(hasSecondPerson)) warn('feedback.reader', `${where}: feedback has no reader in it (no "you"). Write to the learner.`, q.id);
    if (!toCheck.some(hasConsequence)) warn('feedback.consequence', `${where}: feedback names the error but not what it costs in the exam.`, q.id);
  }
  if (feedbackText && wordCount(feedbackText) < 8) warn('feedback.short', `${where}: feedback is ${wordCount(feedbackText)} words; it should teach the why.`, q.id);

  // Paper-profile fields
  if (profile === 'paper') {
    if (!q.sourceKey && q.assessed) warn('question.source', `${where} has no sourceKey; the source panel will be empty beside it.`, q.id);
    if (q.skill && !NSC_SKILLS.includes(q.skill)) block('question.skill', `${where}: unknown skill "${q.skill}".`, q.id);
    if (q.assessed && !q.skill) warn('question.skill', `${where} has no skill, so the coach button has no guide to open.`, q.id);
    if (q.skill === 'reliability' && q.options) {
      const bothSides = q.options.every((o) => /\b(but|however|although|yet|while)\b/i.test(stripHtml(o)));
      if (!bothSides) warn('question.reliability', `${where}: on a reliability question every option should name both a strength and a limitation, or the answer is visible from shape alone.`, q.id);
    }
    if (q.mode === 'mcq' && typeof q.marks === 'number' && q.marks === 4 && !q.acceptAny) {
      // The (2 x 2) note is rendered automatically; just make sure the feedback carries a second point.
      if (!/second|also accepts|another point|as well/i.test(feedbackText)) {
        warn('question.twoByTwo', `${where} is worth 4 marks; the exam expects two points. Name a second creditworthy point in the feedback.`, q.id);
      }
    }
  }
  if (profile === 'check' && q.assessed && (q.slot === undefined || !Number.isInteger(q.slot))) {
    block('question.slot', `${where}: every assessed check question needs a slot number so sittings A and B can be compared.`, q.id);
  }
}

function checkPaper(run: ReviseRun, report: ReviseReport, block: Block, warn: Block) {
  const p = run.paper!;
  const assessed = assessedQuestions(run);
  const split: Record<'L1' | 'L2' | 'L3', number> = { L1: 0, L2: 0, L3: 0 };
  for (const q of assessed) {
    const lv = nscLevelOf(q.tag);
    if (lv) split[`L${lv}` as 'L1' | 'L2' | 'L3'] += typeof q.marks === 'number' ? q.marks : 0;
  }
  report.levelSplit = split;

  if (run.board === 'nsc') {
    const total = split.L1 + split.L2 + split.L3;
    if (total !== p.totalMarks) block('paper.total', `Assessed marks total ${total}, but paper.totalMarks is ${p.totalMarks}.`);
    for (const k of ['L1', 'L2', 'L3'] as const) {
      const drift = Math.abs(split[k] - NSC_LEVEL_TARGET[k]);
      if (drift > 3) block('paper.levels', `${k} carries ${split[k]} marks against a target of ${NSC_LEVEL_TARGET[k]} (drift ${drift}). More than ±3 means the tagging is wrong, not the DBE.`);
      else if (drift >= 2) warn('paper.levels', `${k} carries ${split[k]} marks against ${NSC_LEVEL_TARGET[k]} (drift ${drift}). Strong warning; re-check the memo.`);
      else if (drift === 1) warn('paper.levels', `${k} carries ${split[k]} marks against ${NSC_LEVEL_TARGET[k]} (drift 1). Normal for a DBE paper.`);
    }
  }

  // Freshness: do the questions share vocabulary with the sources?
  const sourceText = (run.sources ?? []).map((s) => s.body ?? '').join(' ');
  const questionText = assessed.map((q) => q.stem).join(' ');
  if (sourceText.trim() && questionText.trim()) {
    const ratio = overlapRatio(questionText, sourceText);
    report.freshness = ratio;
    if (ratio < FRESHNESS_MIN) warn('paper.freshness', `Only ${Math.round(ratio * 100)}% of the question vocabulary appears in the sources. This is what a build from the wrong paper looks like.`);
  }

  // Guides
  const generic = run.board === 'nsc' ? NSC_SKILLS.filter((s) => !p.guideExamples?.[s]?.trim()) : [];
  report.genericGuides = generic;
  if (generic.length) warn('paper.guides', `Skills guides still on the generic example: ${generic.join(', ')}. Write this paper's own weak/strong/difference for each.`);

  const mtas = run.screens.filter((s) => s.type === 'mta');
  if (mtas.length === 1) warn('paper.mta', 'Only one Mark This Answer script; the round is designed around two, close enough in quality that the learner has to think.');
}

function checkLessonBand(run: ReviseRun, report: ReviseReport, warn: Block) {
  const assessed = assessedQuestions(run);
  let eligible = 0;
  let lower = 0;
  let excluded = 0;
  for (const q of assessed) {
    const lo = q.tag ? isLowerOrder(run.board, run.profile, q.tag) : null;
    if (lo === null) {
      excluded++;
      continue;
    }
    eligible++;
    if (lo) lower++;
  }
  report.bandExcluded = excluded;
  if (eligible > 0) {
    const pct = Math.round((lower / eligible) * 100);
    report.lowerOrderPct = pct;
    if (run.board === 'ieb' && pct < IEB_LOWER_ORDER_TARGET - 10) {
      warn('lesson.band', `Lower-order share is ${pct}% against a ${IEB_LOWER_ORDER_TARGET}% target. Write real lower-order items; never tag your way to the band.`);
    }
  }
}

function checkCheck(run: ReviseRun, block: Block, warn: Block) {
  const c = run.check!;
  if (!c.pairKey?.trim()) block('check.pairKey', 'check.pairKey is required so the two sittings can be matched.');
  if (c.sitting !== 'before' && c.sitting !== 'after') block('check.sitting', 'check.sitting must be before | after.');
  const slots = assessedQuestions(run).map((q) => q.slot).filter((s): s is number => typeof s === 'number');
  if (new Set(slots).size !== slots.length) block('check.slots', 'Two assessed questions share a slot number.');
  const nonQuestion = run.screens.filter((s) => s.type !== 'question').length;
  if (nonQuestion > 2) warn('check.screens', `A knowledge check is questions; this one has ${nonQuestion} non-question screens.`);
}

/** "Fifty marks" / "26 marks" on the cover, if the cover states a total. */
export function coverMarksClaim(run: ReviseRun): number | null {
  const text = [run.cover.title, ...run.cover.lines, run.cover.time ?? ''].join(' ');
  const digits = text.match(/\b(\d{1,3})\s*marks?\b/i);
  if (digits) return parseInt(digits[1], 10);
  const words: Record<string, number> = { ten: 10, twelve: 12, fifteen: 15, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80 };
  const m = text.toLowerCase().match(/\b(ten|twelve|fifteen|twenty|thirty|forty|fifty|sixty|seventy|eighty)\s+marks?\b/);
  return m ? words[m[1]] : null;
}
